import PropTypes from 'prop-types';
import { useCallback, useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import {
  CircularProgress,
  Dialog as MuiDialog,
  DialogTitle,
  IconButton,
  Typography,
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import { makeStyles } from '@material-ui/core/styles';
import {
  GENERATE,
  SEND,
  clearGenerated,
  generate,
  send,
} from 'model/oneTimePasscode';
import { LOAD as LOAD_EVENTS } from 'model/oneTimePasscodeEvent';
import { LOAD as LOAD_INFO } from 'model/oneTimePasscodeInfo';
import { useDispatch, useSelector } from 'react-redux';
import { addMessage } from 'reducers/successMessage';
import { selector as busySelector } from 'selectors/busy';
import { selectorGeneratedByPayerId as selector } from 'selectors/oneTimePasscode';
import { selector as oneTimePasscodeInfoSelector } from 'selectors/oneTimePasscodeInfo';
import {
  otpConfig,
  selectUserFspConfigByFspIdPayerId,
} from 'selectors/userFspConfig';
import {
  DIALOG_STEP,
  PAYER_VERIFICATION_OTP_EVENT,
  SEND_TO_SMS,
} from '../consts';
import { getDeliveryEligibility } from '../getDeliveryEligibility';
import DialogActions from './DialogActions';
import DialogContent from './DialogContent';

const useStyles = makeStyles(() => ({
  circularProgress: {
    left: 'calc(50% - 25px)',
    position: 'absolute',
    top: 'calc(50% - 25px)',
    visibility: 'hidden',
  },
  dialogTitle: {
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'space-between',
    padding: '16px 24px',
  },
  visible: {
    visibility: 'visible',
  },
}));

const Dialog = ({ fspId, payerId, userId, ...props }) => {
  const [disabled, setDisabled] = useState(true);
  const [open, setOpen] = useState(true);
  const [otpEvent, setOtpEvent] = useState();
  const [step, setStep] = useState(DIALOG_STEP.CONFIGURE);
  const [sendTo, setSendTo] = useState(SEND_TO_SMS);
  const [stepError, setStepError] = useState(null);
  const [isSendingOtp, setIsSendingOtp] = useState(false);
  const closeTimerRef = useRef();
  const classes = useStyles();
  const dispatch = useDispatch();
  const isGenerateBusy = useSelector((state) => busySelector(state, GENERATE));
  const isLoadEventsBusy = useSelector((state) =>
    busySelector(state, LOAD_EVENTS)
  );
  const isLoadInfoBusy = useSelector((state) => busySelector(state, LOAD_INFO));
  const isSendBusy = useSelector((state) => busySelector(state, SEND));
  const passcode = useSelector((state) => selector(state, payerId));
  const config = useSelector((state) =>
    selectUserFspConfigByFspIdPayerId(state, fspId, payerId)
  );
  const { userInfo } = useSelector(oneTimePasscodeInfoSelector);
  const fspOtpConfig = useSelector((state) => otpConfig(state, fspId, payerId));
  const resolvedUserId = userId ?? config?.userId;
  const isPayerVerification = otpEvent === PAYER_VERIFICATION_OTP_EVENT;
  const title =
    step === DIALOG_STEP.DELIVERY
      ? 'Confirm Delivery Method'
      : 'Generate New OTP';

  const handleClose = useCallback(() => {
    clearTimeout(closeTimerRef.current);
    setOpen(false);
    setDisabled(true);
    setOtpEvent(undefined);
    setStep(DIALOG_STEP.CONFIGURE);
    setStepError(null);
    setSendTo(SEND_TO_SMS);
    setIsSendingOtp(false);
  }, []);

  const handleGenerate = () => {
    setStepError(null);

    if (!resolvedUserId) {
      setStepError('Select a payer user to send OTP');
      return;
    }

    if (otpEvent === PAYER_VERIFICATION_OTP_EVENT) {
      const eligibility = getDeliveryEligibility(userInfo, fspOtpConfig);
      if (eligibility.useGenerateFallback) {
        setDisabled(true);
        dispatch(
          generate({
            data: { otpEvent },
            fspId,
            payerId,
            userId: resolvedUserId,
          })
        );
        return;
      }
      setSendTo(eligibility.defaultSendTo);
      setStep(DIALOG_STEP.DELIVERY);
      return;
    }

    setDisabled(true);
    dispatch(
      generate({ data: { otpEvent }, fspId, payerId, userId: resolvedUserId })
    );
  };

  const handleReasonChange = (event) => {
    setDisabled(false);
    setStepError(null);
    setOtpEvent(event.target.value);
  };

  const handleSendOtp = useCallback(() => {
    setStepError(null);
    setIsSendingOtp(true);
    dispatch(
      send({
        data: {
          otpEvent,
          sendTo,
        },
        fspId,
        onError: (error) => {
          setIsSendingOtp(false);
          setStepError(error?.message || 'Unable to send OTP');
        },
        onSuccess: () => {
          closeTimerRef.current = setTimeout(() => {
            dispatch(
              addMessage(
                'OTP send request submitted. Please refresh the list to view the latest OTP details.'
              )
            );
            handleClose();
          }, 700);
        },
        payerId,
        userId: resolvedUserId,
      })
    );
  }, [dispatch, fspId, handleClose, otpEvent, payerId, resolvedUserId, sendTo]);

  useEffect(() => {
    dispatch(clearGenerated());

    return () => clearTimeout(closeTimerRef.current);
  }, [dispatch]);

  return (
    <MuiDialog
      {...props}
      disableBackdropClick={isPayerVerification || isSendBusy || isSendingOtp}
      disableEscapeKeyDown={isPayerVerification || isSendBusy || isSendingOtp}
      fullWidth
      maxWidth={step === DIALOG_STEP.CONFIGURE ? undefined : 'xs'}
      onClose={handleClose}
      open={open}
    >
      <DialogTitle disableTypography className={classes.dialogTitle}>
        <Typography variant="h6">{title}</Typography>
        <IconButton
          aria-label="close"
          disabled={isSendBusy || isSendingOtp}
          onClick={handleClose}
          size="small"
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <CircularProgress
        className={classNames(classes.circularProgress, {
          [classes.visible]:
            isGenerateBusy || isLoadEventsBusy || isLoadInfoBusy,
        })}
        size={50}
      />
      <DialogContent
        fspId={fspId}
        fspOtpConfig={fspOtpConfig}
        isSendBusy={isSendBusy || isSendingOtp}
        onChange={handleReasonChange}
        onSendToChange={setSendTo}
        payerId={payerId}
        sendTo={sendTo}
        step={step}
        stepError={stepError}
        userId={resolvedUserId}
        userInfo={userInfo}
      />
      <DialogActions
        disabled={disabled}
        isSendBusy={isSendBusy || isSendingOtp}
        onClose={handleClose}
        onGenerate={handleGenerate}
        onSendOtp={handleSendOtp}
        otpEvent={otpEvent}
        passcode={passcode}
        step={step}
      />
    </MuiDialog>
  );
};

Dialog.propTypes = {
  fspId: PropTypes.number.isRequired,
  payerId: PropTypes.number.isRequired,
  userId: PropTypes.number,
};

Dialog.defaultProps = {
  userId: null,
};

export default Dialog;
